import React, { useMemo } from 'react';
import { getManualAssetUrl } from '../../api/manualService';

const ManualSection = ({ section, isLoading, error }) => {
  const transformCallouts = (html) => {
    if (!html) {
      return '';
    }

    const escapeHtml = (value) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const CALL_OUT_ICONS = {
      tip: '💡',
      info: 'ℹ️',
      warning: '⚠️',
      success: '✅',
      note: '📝',
    };

    const normalizeParagraphMarkers = (value) =>
      value
        .replace(/<p>:::([a-z]+)\s*/gi, ':::$1 ')
        .replace(/\s*:::<\/p>/gi, ' :::');

    const formatCalloutContent = (content) => {
      const trimmed = content.trim();
      if (!trimmed) {
        return '';
      }

      const containsHtml = /<[^>]+>/.test(trimmed);
      if (containsHtml) {
        return trimmed;
      }

      return trimmed
        .split(/\n{2,}/)
        .map((paragraph) =>
          `<p>${escapeHtml(paragraph.trim()).replace(/\n/g, '<br />')}</p>`
        )
        .join('');
    };

    const processed = normalizeParagraphMarkers(html);

    return processed.replace(/:::([a-z]+)\s*([\s\S]*?)\s*:::/gi, (match, type, content) => {
      const normalizedType = type.toLowerCase();
      const variant = CALL_OUT_ICONS[normalizedType] ? normalizedType : 'info';
      const icon = CALL_OUT_ICONS[variant];
      const body = formatCalloutContent(content);

      return `
        <div class="manual-callout manual-callout--${variant}">
          <div class="manual-callout__icon" aria-hidden="true">${icon}</div>
          <div class="manual-callout__content">${body}</div>
        </div>
      `;
    });
  };

  const htmlContent = useMemo(() => {
    if (!section) return '';

    if (typeof section.content === 'string') {
      return transformCallouts(section.content);
    }

    if (section.html && typeof section.html === 'string') {
      return transformCallouts(section.html);
    }

    if (section.content && typeof section.content.html === 'string') {
      return transformCallouts(section.content.html);
    }

    if (section.body && typeof section.body === 'string') {
      return transformCallouts(section.body);
    }

    return '';
  }, [section]);

  const assets = useMemo(() => {
    if (!section) return [];
    if (Array.isArray(section.assets)) return section.assets;
    if (section.frontmatter && Array.isArray(section.frontmatter.assets)) {
      return section.frontmatter.assets;
    }
    return [];
  }, [section]);

  if (isLoading) {
    return <div className="manual-section__state">Cargando contenido…</div>;
  }

  if (error) {
    return <div className="manual-section__state manual-section__state--error">{error}</div>;
  }

  if (!section) {
    return <div className="manual-section__state">Selecciona una sección para comenzar.</div>;
  }

  const roles = section.roles || section.frontmatter?.roles || [];
  const tags = section.tags || section.frontmatter?.tags || [];
  const summary = section.summary || section.frontmatter?.summary || '';

  return (
    <article className="manual-section">
      <header className="manual-section__header">
        <div className="manual-section__meta">
          {roles.length > 0 && (
            <ul className="manual-section__roles">
              {roles.map((role) => (
                <li key={role} className="manual-section__role-chip">{role}</li>
              ))}
            </ul>
          )}
          {tags.length > 0 && (
            <ul className="manual-section__tags">
              {tags.map((tag) => (
                <li key={tag} className="manual-section__tag-chip">#{tag}</li>
              ))}
            </ul>
          )}
        </div>
        <h1 className="manual-section__title">{section.title}</h1>
        {summary && <p className="manual-section__summary">{summary}</p>}
      </header>

      {htmlContent && (
        <div
          className="manual-section__content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}

      {assets.length > 0 && (
        <section className="manual-section__assets">
          <h2 className="manual-section__assets-title">Recursos relacionados</h2>
          <div className="manual-section__assets-grid">
            {assets.map((assetPath) => {
              const assetUrl = getManualAssetUrl(assetPath);
              const extension = assetPath.split('.').pop()?.toLowerCase();
              const isVideo = extension === 'mp4' || extension === 'webm' || extension === 'ogg';

              return (
                <div key={assetPath} className="manual-section__asset">
                  {isVideo ? (
                    <video controls preload="metadata" src={assetUrl}>
                      <track kind="captions" />
                    </video>
                  ) : (
                    <img src={assetUrl} alt={assetPath} loading="lazy" />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </article>
  );
};

export default ManualSection;

